'use client';

import Cookies from 'js-cookie';
import { ReadonlyURLSearchParams } from 'next/navigation';

import React, {
    ReactNode,
    createContext,
    memo,
    useContext,
    useEffect,
    useState,
} from 'react';

import {
    GRS_SEARCH_PROVIDER_QUERY_PARAM_VALUE,
    SEARCH_PROVIDER_QUERY_PARAM_KEY,
    SEARCH_SERVICE_PROVIDERS,
} from '@/constants/grs';
import { ADOBE_SESSION_ID_KEY } from '@/src/components/Adobe/constants';
import useGrsPersonalization from '@/src/hooks/useGrsPersonalization';
import type {
    ContentStackEntryDataProps,
    GRSFacetConfigProperties,
    GrsConfig,
    SearchPageConfigProperties,
    SearchServiceConfig,
} from '@/src/types/contentStack';
import { SearchParams } from '@/src/types/searchQuery';
import { PageType } from '@/types/pages/search';
import {
    getAdobeSessionId,
    getIsGRSSearchProviderSessionStorageSet,
    setGRSSearchProviderSessionStorage,
} from '@/utils/grs';
import type { LocaleProps } from '@nxt-costco-com/forge-digital-components';
import {
    createUUID,
    useBrowseContext,
    useLocalForage,
    useQueryParams,
    useSearchProvider,
} from '@nxt-costco-com/forge-digital-components';
import { logger } from '@nxt-costco-com/forge-digital-components';

import { DEFAULT_USER_STATE } from './constants';
import { GrsQuery, isSearchResultGRS } from './services/Grs';
import { LucidWorksQuery, isSearchResultLW } from './services/LucidWorks';
import type {
    SearchContextType,
    SearchResult,
    WarehouseCookieValue,
} from './types';
import {
    checkForGrsItemNumberRedirect,
    checkForLucidWorksItemNumberRedirect,
    getLocationsFromCookie,
    isBDSite,
    overrideSearchProvider,
    updatePageTitle,
} from './utils';

// Create the context with a default value
const SearchContext = createContext<SearchContextType | undefined>(undefined);

type SearchProviderProps = {
    lang: LocaleProps;
    config: ContentStackEntryDataProps;
    serviceConfig: SearchPageConfigProperties;
    children: ReactNode;
    requestHeaders: SearchServiceConfig['required_request_headers'];
    NoResultsComponent: ReactNode;
    loc?: string;
    whloc?: string;
    userZipCode?: string;
    userLocation?: string;
    pageType: PageType;
    isEnabled?: boolean;
    enableLivePreview?: boolean;
    livePreviewKeyword?: string;
    mdo?: string;
    site: string;
    grsConfig: GrsConfig | undefined;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    isGRSFeatureFlagEnabled: boolean;
    isGRSBetaReleaseFeatureFlagEnabled: boolean;
    categoryPageId: string | undefined;
    searchFsaExemptKeywords: string[] | undefined;
    categoryFsaFlag: boolean;
    isCategoryPage?: boolean;
};

export const SearchProviderComponent = ({
    lang,
    serviceConfig,
    requestHeaders,
    children,
    NoResultsComponent,
    loc,
    whloc,
    userZipCode,
    userLocation = DEFAULT_USER_STATE,
    mdo,
    pageType,
    isEnabled = true,
    enableLivePreview,
    livePreviewKeyword,
    site,
    grsConfig,
    grsFacetConfig,
    isGRSFeatureFlagEnabled,
    isGRSBetaReleaseFeatureFlagEnabled,
    categoryPageId,
    searchFsaExemptKeywords,
    categoryFsaFlag,
    isCategoryPage,
}: SearchProviderProps) => {
    const { isInitialized: isQueryParamsLoaded, queryParams: searchParams } =
        useQueryParams();

    const [searchResult, setSearchResult] = useState<
        SearchResult | undefined
    >();
    const [searchedParams, setSearchedParams] = useState<
        SearchParams | undefined
    >();
    const [isSearchResultsLoading, setIsSearchResultsLoading] =
        useState<boolean>(true);

    const searchServiceProvider = useSearchProvider({
        isGRSFeatureFlagEnabled,
        isGRSBetaReleaseFeatureFlagEnabled,
    });
    // This is GRS specific. For modifying GRS personalization request param passed to api based on QP.
    const isGRSPersonalizationEnabled = useGrsPersonalization();

    // Temporary routing till CLP development is done. Treats CLP pages always as LUCIDWORKS
    const searchService =
        overrideSearchProvider(searchParams, isCategoryPage) ||
        searchServiceProvider;

    const [sortType, setSortType] = useState<string>('');
    const {
        data: adobeSessionIdLocalForage,
        error: adobeLocalForageError,
        isStoreInitialized: isAdobeLocalForageInitialzed,
    } = useLocalForage<string>(ADOBE_SESSION_ID_KEY, createUUID());

    const adobeSessionID = getAdobeSessionId({
        searchService,
        adobeSessionIdLocalForage,
        hasLocalForageError: !!adobeLocalForageError,
        isLocalForageInitialzed: isAdobeLocalForageInitialzed,
    });

    const searchProviderQueryParam = searchParams.get(
        SEARCH_PROVIDER_QUERY_PARAM_KEY,
    );

    const isSearchProviderQueryParamGRS =
        searchProviderQueryParam?.toLowerCase() ===
        GRS_SEARCH_PROVIDER_QUERY_PARAM_VALUE;

    // Set searchProvider session storage value for beta release
    useEffect(() => {
        const isGRSSearchProviderSessionStorageSet =
            getIsGRSSearchProviderSessionStorageSet();

        if (
            isSearchProviderQueryParamGRS &&
            !isGRSSearchProviderSessionStorageSet
        ) {
            setGRSSearchProviderSessionStorage();
        }
    }, [isSearchProviderQueryParamGRS]);

    useEffect(() => {
        const run = async () => {
            setIsSearchResultsLoading(true);
            try {
                // Needed to send to GRS request for personalization. User hashed Id present in cookies set by WCS.
                const userInfoId = Cookies.get('costcoHashId');
                let effectiveSearchParams = searchParams;

                if (enableLivePreview && livePreviewKeyword) {
                    const paramsObject = searchParams
                        ? Object.fromEntries(searchParams.entries())
                        : {};
                    paramsObject['keyword'] = livePreviewKeyword;
                    effectiveSearchParams = new URLSearchParams(
                        paramsObject,
                    ) as ReadonlyURLSearchParams;
                }

                let searchResult: SearchResult;
                switch (searchService) {
                    case SEARCH_SERVICE_PROVIDERS.LUCIDWORKS:
                        searchResult = await LucidWorksQuery(
                            effectiveSearchParams,
                            lang,
                            requestHeaders,
                            serviceConfig,
                            { loc, whloc, userLocation, mdo },
                            pageType,
                            site,
                        );
                        break;
                    case SEARCH_SERVICE_PROVIDERS.GRS:
                        searchResult = await GrsQuery(
                            grsConfig,
                            grsFacetConfig,
                            effectiveSearchParams,
                            lang,
                            serviceConfig,
                            {
                                loc,
                                whloc,
                                userLocation,
                                mdo,
                                userZipCode,
                            },
                            pageType,
                            site,
                            adobeSessionID ?? '',
                            categoryPageId,
                            searchFsaExemptKeywords,
                            categoryFsaFlag,
                            isGRSPersonalizationEnabled,
                            userInfoId,
                        );
                        break;
                    default:
                        throw new Error(
                            `Search service "${searchService}" not supported`,
                        );
                }

                const kw = effectiveSearchParams.get('keyword')!;
                // checking for redirect based on GRS
                const grsRedirect =
                    searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
                    checkForGrsItemNumberRedirect(kw, searchResult);

                const lucidWorksRedirect =
                    searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
                    checkForLucidWorksItemNumberRedirect(kw, searchResult);

                // if GRS is found then use GRS as priority else check for LucidWorks
                if (grsRedirect) {
                    window.location.href = grsRedirect;
                    return;
                }

                if (lucidWorksRedirect) {
                    window.location.href = lucidWorksRedirect;
                    return;
                }

                if (searchResult?.redirect) {
                    /* Bug 114623: in case of category page redirect, onclick of back button was not navigating user to page it came from */
                    // replace method of location is used so that it doesn't save the current request in the browser history
                    window.location.replace(searchResult.redirect);

                    return;
                }

                updatePageTitle(effectiveSearchParams, searchResult, kw);
                setSearchResult(searchResult);

                setSearchedParams(
                    Object.fromEntries(effectiveSearchParams.entries()),
                );
                setIsSearchResultsLoading(false);
            } catch (err) {
                logger.error(`Error occurred: ${err}`);
                setIsSearchResultsLoading(false);
            }
        };

        if (loc && (isBDSite(site) || userLocation) && searchService) {
            if (
                searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
                // adobeSessionId will be null until read from indexedDB
                // GRS search api throws error if whloc is not set
                (!adobeSessionID || !whloc)
            ) {
                // Skip search run if localforage is not initialized as wrong adobe session id will be used
                logger.warn(`Skipping search run, awaiting initialization`);
            } else if (
                searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
                isGRSFeatureFlagEnabled &&
                isGRSBetaReleaseFeatureFlagEnabled &&
                isSearchProviderQueryParamGRS
            ) {
                // Handle case where Lucidworks user switches to GRS but searchProvider has not yet updated
                logger.warn(
                    `Skipping search run, awaiting provider initialization`,
                );
            } else {
                if (isQueryParamsLoaded) {
                    run();
                }
            }
        } else {
            logger.warn(
                `Skipping search run, loc: ${loc} | ul: ${userLocation}`,
            );
        }
    }, [
        adobeSessionID,
        categoryPageId,
        enableLivePreview,
        isGRSBetaReleaseFeatureFlagEnabled,
        isGRSFeatureFlagEnabled,
        isSearchProviderQueryParamGRS,
        pageType,
        lang,
        livePreviewKeyword,
        loc,
        requestHeaders,
        searchParams,
        searchService,
        serviceConfig,
        userLocation,
        whloc,
        site,
        grsConfig,
        grsFacetConfig,
        userZipCode,
        mdo,
        categoryFsaFlag,
        searchFsaExemptKeywords,
        isQueryParamsLoaded,
        isGRSPersonalizationEnabled,
    ]);

    // Search is actively enabled, or a refinement tag is present on a search disabled page
    const isSearchActive = isEnabled || !!searchParams.get('refine');
    if (isEnabled === false) {
        return (
            <SearchContext.Provider
                value={{
                    searchResult,
                    isSearchResultsLoading,
                    searchedParams,
                    searchService,
                    sortType,
                    setSortType,
                }}
            >
                {children}
                <div
                    dangerouslySetInnerHTML={{
                        __html: `<!-- 
                        resultsLeadToFSACHDI: ${searchResult?.isAdTargetingExplicitlyDisabled ? 1 : 0}
                        isFSACHDIExceptionSet: ${searchResult?.isAdTargetingExceptionEnabled ? 1 : 0}
                        doNotTrackLayout: ${!searchResult?.isAdTargetingEnabled}
                    -->`,
                    }}
                />
            </SearchContext.Provider>
        );
    }

    const hasLWResults =
        searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
        isSearchResultLW(searchResult) &&
        searchResult?.docs?.length > 0;

    const hasGRSResults =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
        isSearchResultGRS(searchResult) &&
        searchResult?.searchResult?.results?.length > 0;

    const isNoResultsComponentUsed =
        isSearchActive &&
        !isSearchResultsLoading &&
        (!searchResult || (!hasLWResults && !hasGRSResults));

    return (
        <SearchContext.Provider
            value={{
                searchResult,
                isSearchResultsLoading,
                searchedParams,
                searchService,
                sortType,
                setSortType,
            }}
        >
            {isNoResultsComponentUsed ? NoResultsComponent : children}
            <div
                dangerouslySetInnerHTML={{
                    __html: `<!-- 
                        resultsLeadToFSACHDI: ${searchResult?.isAdTargetingExplicitlyDisabled ? 1 : 0}
                        isFSACHDIExceptionSet: ${searchResult?.isAdTargetingExceptionEnabled ? 1 : 0}
                        doNotTrackLayout: ${!searchResult?.isAdTargetingEnabled}
                    -->`,
                }}
            />
        </SearchContext.Provider>
    );
};

const MemoizedSearchProviderComponent = memo(SearchProviderComponent);

// Create the provider component
export const SearchProvider = (props: SearchProviderProps) => {
    const {
        warehouse,
        deliveryLocation,
        bdDeliveryLocation,
        bdWarehouseNumber,
    } = useBrowseContext();

    // TODO: Use this from context over deriving it manually like this.
    let { loc, whloc, mdo } = getLocationsFromCookie(
        warehouse as WarehouseCookieValue,
    );
    let { state, zipCode } = deliveryLocation || {};

    // BD does not use the whloc param, and uses the BD warehouse cookie for loc instead
    if (isBDSite(props.site)) {
        whloc = '';
        loc =
            bdWarehouseNumber?.value || props.serviceConfig.defaultBDWarehouse;

        if (/[a-zA-Z]/.test(bdDeliveryLocation?.bdLocation ?? '')) {
            // State Location is valid, use the last two characters as state code
            state = bdDeliveryLocation?.bdLocation?.slice(-2);
        } else if (props.lang === 'en-ca' || props.lang === 'fr-ca') {
            // For Canada BD, we cannot use the default value. If state code is not valid, we set it to undefined
            state = undefined;
        }
    }

    return (
        <MemoizedSearchProviderComponent
            loc={loc}
            whloc={whloc}
            userLocation={state}
            userZipCode={zipCode}
            mdo={mdo}
            {...props}
        />
    );
};

// Create a custom hook to use the SearchContext
export const useSearchContext = (): SearchContextType => {
    const context = useContext(SearchContext);
    if (context === undefined) {
        throw new Error(
            'useSearchContext must be used within a SearchProvider',
        );
    }
    return context;
};
